import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiClient, SESSION_EXPIRED_EVENT } from '../utils/api';
import {
  signIn as cognitoSignIn,
  signUp as cognitoSignUp,
  signOut as cognitoSignOut,
  getAccessToken,
} from '../utils/cognito';

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  tier_name?: string;
  /** "sandbox" = Plaid demo data; "production" = real bank accounts. Admin-granted. */
  plaid_mode?: 'sandbox' | 'production';
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: { email: string; password: string; first_name: string; last_name: string }) => Promise<void>;
  /** Called by /auth/callback after a social sign-in has seeded a Cognito session. */
  completeSocialLogin: () => Promise<void>;
  logout: () => Promise<void>;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // The ApiClient singleton attaches a fresh Cognito token to every request and,
  // on any 401, fires SESSION_EXPIRED_EVENT. This context owns the auth-expiry
  // pathway: it clears state here, and ProtectedRoute redirects to /login.
  useEffect(() => {
    const onSessionExpired = () => {
      setUser(null);
      setIsAuthenticated(false);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, []);

  // Fetch the authenticated user's profile. A 401 is handled by the interceptor.
  const fetchUser = useCallback(async (): Promise<boolean> => {
    try {
      const response = await apiClient.getMe();
      setUser(response.data);
      setIsAuthenticated(true);
      return true;
    } catch {
      return false;
    }
  }, []);

  // On app start, adopt any existing Cognito session (SDK persists it in
  // localStorage; getAccessToken silently refreshes an expired access token).
  useEffect(() => {
    const init = async () => {
      const token = await getAccessToken();
      if (token) {
        await fetchUser();
      }
      setIsLoading(false);
    };
    init();
  }, [fetchUser]);

  // Warm up the backend session (Plaid refresh + Redis) then load the profile.
  // Shared by every sign-in path so the flow can't drift.
  const afterSignIn = useCallback(async () => {
    try {
      await apiClient.createSession();
    } catch {
      // A failed warm-up must not block login — the dashboard will refresh data
      // on its own. The session is still valid.
    }
    await fetchUser();
  }, [fetchUser]);

  const login = async (email: string, password: string) => {
    await cognitoSignIn(email, password); // throws on bad credentials / unconfirmed
    await afterSignIn();
  };

  const register = async (userData: { email: string; password: string; first_name: string; last_name: string }) => {
    // Cognito emails a verification code; the account isn't usable until
    // confirmed, so registration does NOT sign the user in here. The Register
    // component routes to the confirm-code screen, which then calls login().
    await cognitoSignUp(userData.email, userData.password, userData.first_name, userData.last_name);
  };

  const completeSocialLogin = async () => {
    // The /auth/callback page has already exchanged the code and seeded the
    // Cognito session; just warm up and load the profile.
    await afterSignIn();
  };

  const logout = async () => {
    try {
      await apiClient.logout(); // backend GlobalSignOut + Redis clear
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      cognitoSignOut();         // clear the local Cognito session
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const value: AuthContextType = {
    user,
    login,
    register,
    completeSocialLogin,
    logout,
    isLoading,
    isAuthenticated,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
